import React, { useState } from "react";
import PageShell from "../dashboard/PageShell";
import { FileDown, Search, Trash2, Pencil } from "lucide-react";

interface ImplementationItem {
  id: string;
  stt: number;
  content: string;
  duration: string;
  assignee: string;
  status: "Đang thực hiện" | "Hoàn thành" | "Chậm" | "Tạm dừng";
  reason?: string;
  notes?: string;
  attachment?: string;
}

const mockData: ImplementationItem[] = [
  {
    id: "1",
    stt: 1,
    content: "Xây dựng bộ câu hỏi khảo sát hiện trạng hệ thống",
    duration: "09/2025",
    assignee: "Nguyễn Văn A",
    status: "Đang thực hiện",
    notes: "Dự kiến hoàn thành tuần 3",
  },
  {
    id: "2",
    stt: 2,
    content: "Thử nghiệm mô hình dự báo mưa",
    duration: "09-10/2025",
    assignee: "Trần Thị B",
    status: "Chậm",
    reason: "Thiết bị lỗi cần thay thế",
  },
];

const statusColor: Record<ImplementationItem["status"], string> = {
  "Đang thực hiện": "text-blue-600",
  "Hoàn thành": "text-green-600",
  Chậm: "text-red-600",
  "Tạm dừng": "text-orange-600",
};

const ProjectImplementation: React.FC = () => {
  const [status, setStatus] = useState<string>("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [rowToDelete, setRowToDelete] = useState<ImplementationItem | null>(
    null
  );

  return (
    <PageShell title="Tiến độ thực hiện đề tài">
      <div className="space-y-6">
        {/* Header Section */}
        <div className="flex justify-between items-center">
          <h1 className="text-xl font-semibold text-gray-800">
            Tiến độ thực hiện đề tài
          </h1>
        </div>
        {/* Search and Filter Section */}
        <div className="bg-white rounded-lg p-4">
          <div className="space-y-4">
            {/* Row 1 - Search input + Search button (kept for consistency) */}
            <div className="flex items-center space-x-4">
              <div className="relative w-96">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <input
                  type="text"
                  placeholder="Tìm kiếm nội dung công việc"
                  className="w-full pl-10 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <button className="bg-green-600 text-white px-3 py-2 rounded text-sm font-medium hover:bg-green-700 transition-colors">
                Tìm kiếm
              </button>
            </div>

            {/* Row 2 - Only status filter */}
            <div className="flex items-center space-x-4">
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="border border-gray-300 rounded-md px-2 py-2 text-sm bg-white"
              >
                <option>- Trạng thái -</option>
                <option>Đang thực hiện</option>
                <option>Hoàn thành</option>
                <option>Chậm</option>
                <option>Tạm dừng</option>
              </select>
            </div>

            {/* Row 3 - Export button */}
            <div className="flex justify-start">
              <button className="bg-orange-500 text-white px-3 py-2 rounded text-sm font-medium hover:bg-orange-600 transition-colors flex items-center space-x-2">
                <FileDown className="h-4 w-4" />
                <span>Xuất ra file</span>
              </button>
            </div>
          </div>
        </div>

        {/* Data Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-16">
                    Stt
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-[420px]">
                    Nội dung công việc - Sản phẩm của đề tài
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-40">
                    Thời gian thực hiện
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-40">
                    Người thực hiện
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-32">
                    Trạng thái
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-48">
                    Lý do chậm
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-40">
                    Ghi chú
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-40">
                    File đính kèm
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider w-24">
                    Chức năng
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {mockData
                  .filter((r) =>
                    status && status !== "- Trạng thái -"
                      ? r.status === status
                      : true
                  )
                  .map((item) => (
                    <tr key={item.id} className="hover:bg-gray-50">
                      <td className="px-2 py-4 whitespace-nowrap text-sm font-medium text-gray-900 border-r border-gray-200 w-16">
                        {item.stt}
                      </td>

                      <td className="px-2 py-4 text-sm text-gray-900 border-r border-gray-200">
                        <div className="leading-tight text-sm">
                          {item.content}
                        </div>
                      </td>
                      <td className="px-2 py-4 text-sm font-medium text-gray-900 border-r border-gray-200">
                        {item.duration}
                      </td>
                      <td className="px-2 py-4 text-sm font-medium text-gray-900 border-r border-gray-200">
                        {item.assignee}
                      </td>
                      <td
                        className={`px-2 py-4 text-sm font-semibold border-r border-gray-200 ${
                          statusColor[item.status]
                        }`}
                      >
                        {item.status}
                      </td>
                      <td className="px-2 py-4 text-sm text-gray-900 border-r border-gray-200">
                        <div className="leading-tight text-sm">
                          {item.reason || ""}
                        </div>
                      </td>
                      <td className="px-2 py-4 text-sm text-gray-900 border-r border-gray-200">
                        <div className="leading-tight text-sm">
                          {item.notes || ""}
                        </div>
                      </td>
                      <td className="px-2 py-4 text-sm text-blue-600 border-r border-gray-200">
                        {item.attachment || ""}
                      </td>
                      <td className="px-2 py-4 text-sm font-medium text-gray-900 w-24">
                        <div className="flex items-center space-x-2">
                          <button className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded">
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => {
                              setRowToDelete(item);
                              setShowDeleteConfirm(true);
                            }}
                            className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>

          {/* Pagination inside the table container */}
          <div className="flex justify-between items-center p-4 border-t border-gray-200">
            <div className="text-sm font-medium text-gray-700">
              Hiển thị <span className="font-bold">1</span> đến{" "}
              <select className="font-bold text-blue-600 border-none bg-transparent focus:ring-0 focus:outline-none text-sm">
                <option>10</option>
                <option>20</option>
                <option>50</option>
                <option>100</option>
              </select>{" "}
              trong <span className="font-bold">{mockData.length}</span> kết quả
            </div>
            <div className="flex space-x-2">
              <button className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                &lt;
              </button>
              <button className="px-3 py-2 text-sm font-medium text-white bg-[#337ab7] border border-[#337ab7] rounded-md">
                1
              </button>
              <button className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                &gt;
              </button>
            </div>
          </div>
        </div>

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100000]">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0 w-10 h-10 mx-auto bg-red-100 rounded-full flex items-center justify-center">
                  <Trash2 className="h-6 w-6 text-red-600" />
                </div>
              </div>
              <div className="text-center">
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Xác nhận xóa
                </h3>
                <p className="text-sm text-gray-500 mb-6">
                  Bạn có chắc chắn muốn xóa mục này không? Hành động này không
                  thể hoàn tác.
                </p>
                <div className="flex space-x-3 justify-center">
                  <button
                    onClick={() => {
                      setShowDeleteConfirm(false);
                      setRowToDelete(null);
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Hủy
                  </button>
                  <button
                    onClick={() => {
                      // TODO: integrate delete API using rowToDelete?.id
                      setShowDeleteConfirm(false);
                      setRowToDelete(null);
                    }}
                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  >
                    Xóa
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </PageShell>
  );
};

export default ProjectImplementation;
